/**
 * Collection — AJAX filtering, sorting, pagination, quick add.
 *
 * CRITICAL: Targeted morph — grid, toolbar, pagination only.
 * The filter drawer stays untouched so it doesn't close.
 */
import { Component } from '@theme/component';
import { fetchConfig, debounce } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

class CollectionComponent extends Component {
  #abortController = null;
  #infiniteObserver = null;

  get sectionId() {
    return this.dataset.sectionId;
  }

  get paginationType() {
    return this.dataset.paginationType || 'pagination';
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#onFilterChange);
    this.addEventListener('click', this.#onClick);

    if (this.paginationType === 'infinite') {
      this.#initInfiniteScroll();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#infiniteObserver?.disconnect();
  }

  // ── Filter/sort change ──

  #onFilterChange = debounce((event) => {
    const t = event.target;
    if (
      t.matches('[data-filter-checkbox]') ||
      t.matches('[data-input-min]') ||
      t.matches('[data-input-max]') ||
      t.matches('[data-range-min]') ||
      t.matches('[data-range-max]') ||
      t.matches('[data-sort-select]')
    ) {
      this.#fetchAndUpdate(this.#buildURL());
    }
  }, 350);

  // ── Click delegation ──

  #onClick = (event) => {
    const target = event.target;

    // Pagination links
    const pageLink = target.closest('[data-pagination-link]');
    if (pageLink) { event.preventDefault(); this.#fetchAndUpdate(pageLink.href); return; }

    // Filter remove pills
    const removeLink = target.closest('[data-filter-remove]');
    if (removeLink) { event.preventDefault(); this.#fetchAndUpdate(removeLink.href); return; }

    // Clear all filters
    const clearLink = target.closest('[data-clear-all]');
    if (clearLink) { event.preventDefault(); this.#fetchAndUpdate(clearLink.href); return; }

    // Load more
    const loadMore = target.closest('[data-load-more]');
    if (loadMore) { this.#onLoadMore(loadMore); return; }

    // Quick add
    const quickAdd = target.closest('[data-quick-add]');
    if (quickAdd) { event.preventDefault(); this.#onQuickAdd(quickAdd); return; }
  };

  // ── Load More ──

  async #onLoadMore(btn) {
    const nextUrl = btn.dataset.nextUrl;
    if (!nextUrl) return;

    btn.disabled = true;
    btn.textContent = '...';

    try {
      const html = await this.#fetchSection(nextUrl);
      if (!html) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newGrid = doc.querySelector('[data-product-grid]');
      const currentGrid = this.querySelector('[data-product-grid]');
      if (newGrid && currentGrid) {
        currentGrid.insertAdjacentHTML('beforeend', newGrid.innerHTML);
      }

      this.#replaceElement('[data-pagination]', doc);

      if (this.paginationType === 'infinite') {
        this.#infiniteObserver?.disconnect();
        this.#initInfiniteScroll();
      }

      window.history.replaceState({}, '', nextUrl);
    } catch (err) {
      console.error('Load more error:', err);
      btn.disabled = false;
    }
  }

  // ── Infinite Scroll ──

  #initInfiniteScroll() {
    const btn = this.querySelector('[data-load-more][data-infinite]');
    if (!btn) return;

    this.#infiniteObserver = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) btn.click(); },
      { rootMargin: '400px' }
    );
    this.#infiniteObserver.observe(btn);
  }

  // ── Quick Add ──

  async #onQuickAdd(btn) {
    btn.disabled = true;
    btn.classList.add('is-loading');

    try {
      const cartDrawer = document.querySelector('cart-drawer-component');
      const drawerSectionId = cartDrawer?.dataset.sectionId || '';

      const body = JSON.stringify({
        items: [{ id: parseInt(btn.dataset.variantId, 10), quantity: 1 }],
        sections: drawerSectionId,
        sections_url: window.location.pathname,
      });

      const res = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const data = await res.json();

      if (!res.ok || data.errors) {
        btn.classList.remove('is-loading');
        btn.disabled = false;
        return;
      }

      document.dispatchEvent(new CartAddEvent(data, null, {
        sections: data.sections,
        source: 'quick-add',
      }));

      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      setTimeout(() => {
        btn.classList.remove('is-added');
        btn.disabled = false;
      }, 2000);
    } catch (err) {
      console.error('Quick add error:', err);
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  }

  // ── Core: fetch + targeted replace ──

  async #fetchAndUpdate(url) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const grid = this.querySelector('[data-product-grid]');
    if (grid) grid.style.opacity = '0.4';

    try {
      const html = await this.#fetchSection(url);
      if (!html) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Replace ONLY these elements — filter drawer stays open
      this.#replaceElement('[data-product-grid]', doc);
      this.#replaceElement('[data-products-count]', doc);
      this.#replaceElement('[data-active-filters]', doc);
      this.#replaceElement('[data-pagination]', doc);
      this.#replaceElement('[data-empty-state]', doc);

      // Update filter form checkboxes/counts inside the drawer
      this.#replaceElement('[data-filter-form]', doc);

      window.history.pushState({}, '', url);
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });

      if (this.paginationType === 'infinite') {
        this.#infiniteObserver?.disconnect();
        this.#initInfiniteScroll();
      }
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Collection fetch error:', err);
    }
  }

  #replaceElement(selector, doc) {
    const current = this.querySelector(selector);
    const incoming = doc.querySelector(selector);

    if (current && incoming) {
      current.innerHTML = incoming.innerHTML;
      // Remove attributes no longer present
      for (const attr of [...current.attributes]) {
        if (!incoming.hasAttribute(attr.name)) {
          current.removeAttribute(attr.name);
        }
      }
      // Copy all incoming attributes
      for (const attr of incoming.attributes) {
        current.setAttribute(attr.name, attr.value);
      }
      current.style.opacity = '';
    } else if (current && !incoming) {
      current.remove();
    }
  }

  async #fetchSection(url) {
    const sectionUrl = new URL(url, location.origin);
    sectionUrl.searchParams.set('section_id', this.sectionId);

    const res = await fetch(sectionUrl.toString(), {
      signal: this.#abortController?.signal,
    });
    if (!res.ok) return null;
    return res.text();
  }

  /**
   * Build URL from filter form + sort
   * Shopify price filters expect values in cents (e.g. 1000 for €10)
   */
  #buildURL() {
    const url = new URL(window.location.origin + window.location.pathname);

    // Preserve search query params (q, type) for search pages
    const currentParams = new URLSearchParams(window.location.search);
    if (currentParams.has('q')) url.searchParams.set('q', currentParams.get('q'));
    if (currentParams.has('type')) url.searchParams.set('type', currentParams.get('type'));

    const sort = this.querySelector('[data-sort-select]');
    if (sort?.value) url.searchParams.set('sort_by', sort.value);

    const form = this.querySelector('[data-filter-form]');
    if (!form) return url.toString();

    // Checkboxes
    form.querySelectorAll('[data-filter-checkbox]:checked').forEach((cb) => {
      url.searchParams.append(cb.name, cb.value);
    });

    // Price range — inputs are in display currency, Shopify needs cents
    const minInput = form.querySelector('[data-input-min]');
    const maxInput = form.querySelector('[data-input-max]');
    const absMin = minInput ? parseFloat(minInput.min) : 0;
    const absMax = maxInput ? parseFloat(maxInput.max) : 0;

    if (minInput?.value && parseFloat(minInput.value) > absMin) {
      url.searchParams.set(minInput.dataset.paramName, Math.round(parseFloat(minInput.value)).toString());
    }
    if (maxInput?.value && parseFloat(maxInput.value) < absMax) {
      url.searchParams.set(maxInput.dataset.paramName, Math.round(parseFloat(maxInput.value)).toString());
    }

    return url.toString();
  }
}

if (!customElements.get('collection-component')) {
  customElements.define('collection-component', CollectionComponent);
}